import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, BarChart2, Pause, Pencil, Play, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { fetchCampaigns, removeAutomatedCampaign, setAutomatedCampaignActive } from '@/features/campaigns/api/use-campaigns';
import { useCampaignScope } from '@/features/campaigns/model/campaign-scope';
import { CampaignListPage } from '@/features/marketing-shared/ui/campaign-list-page';
import { useConfirm } from '@/shared/components/confirm';
import { Button } from '@/shared/ui/button';
import type { Campaign } from '@/shared/types/campaign';
import { isPushCampaign, toPushRow } from '../model/map-campaign';

/** Única ou com expiração: tem tela de resultados. */
function hasResults(campaign: Campaign): boolean {
  return campaign.expirar === true || campaign.frequencia === '31';
}

function PushActions({
  campaign,
  onEdit,
  onPause,
  onRemove,
  onResults,
}: {
  campaign: Campaign;
  onEdit?: () => void;
  onPause?: () => void;
  onRemove?: () => void;
  onResults?: () => void;
}) {
  const paused = campaign.active === false;
  return (
    <div className="flex items-center gap-1">
      {onEdit ? (
        <Button type="button" variant="ghost" size="icon-sm" aria-label="Editar" onClick={onEdit}>
          <Pencil />
        </Button>
      ) : null}
      {onPause ? (
        <Button type="button" variant="ghost" size="icon-sm" aria-label={paused ? 'Ativar' : 'Pausar'} onClick={onPause}>
          {paused ? <Play /> : <Pause />}
        </Button>
      ) : null}
      {onResults ? (
        <Button type="button" variant="ghost" size="icon-sm" aria-label="Resultados" onClick={onResults}>
          <BarChart2 />
        </Button>
      ) : null}
      {onRemove ? (
        <Button type="button" variant="ghost" size="icon-sm" aria-label="Excluir" onClick={onRemove}>
          <Trash2 />
        </Button>
      ) : null}
    </div>
  );
}

export function PushCampaignsPage() {
  const { idPassbook, listPath, organizationName } = useCampaignScope();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const query = useQuery({
    queryKey: ['campaigns', 'automated', idPassbook],
    queryFn: () => fetchCampaigns(idPassbook, 'automated') as Promise<Campaign[]>,
    staleTime: 60_000,
  });

  const campaigns = useMemo(() => (query.data ?? []).filter(isPushCampaign), [query.data]);
  const byId = useMemo(() => new Map(campaigns.map((campaign) => [campaign._id, campaign])), [campaigns]);
  const rows = useMemo(() => campaigns.map(toPushRow), [campaigns]);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['campaigns', 'automated', idPassbook] });
  }, [idPassbook, queryClient]);

  const setActive = useCallback(
    async (campaign: Campaign, ativo: boolean) => {
      const proceed = await confirm({
        title: ativo ? 'Ativar campanha' : 'Pausar campanha',
        description: ativo
          ? 'Ela volta a disparar no horário que já está salvo.'
          : 'Ela deixa de disparar até ser ativada de novo. Horário, público e mensagem continuam os mesmos.',
        confirmLabel: ativo ? 'Ativar' : 'Pausar',
        variant: ativo ? 'default' : 'destructive',
      });
      if (!proceed) return;
      try {
        await setAutomatedCampaignActive(idPassbook, campaign._id, ativo);
        toast.success(ativo ? 'Campanha ativada.' : 'Campanha pausada.');
        refresh();
      } catch {
        toast.error(ativo ? 'Falha ao ativar.' : 'Falha ao pausar.');
      }
    },
    [confirm, idPassbook, refresh],
  );

  const removeOne = useCallback(
    async (campaign: Campaign) => {
      const proceed = await confirm({
        title: 'Excluir campanha',
        description: 'A campanha deixa de existir e não dispara mais. Esta ação não pode ser desfeita.',
        confirmLabel: 'Excluir',
        variant: 'destructive',
      });
      if (!proceed) return;
      try {
        await removeAutomatedCampaign(idPassbook, campaign._id);
        toast.success('Campanha excluída.');
        refresh();
      } catch {
        toast.error('Falha ao excluir.');
      }
    },
    [confirm, idPassbook, refresh],
  );

  return (
    <CampaignListPage
      title="Push"
      description={organizationName}
      icon={Bell}
      phase={query.isLoading ? 'loading' : query.isError ? 'error' : 'ready'}
      loadingLabel="Carregando campanhas de push"
      errorTitle="Não foi possível carregar as campanhas"
      rows={rows}
      emptyTitle="Nenhuma campanha de push"
      emptyDescription="Crie a primeira para agendar um disparo no aplicativo."
      selectAllId="select-all-push-campaigns"
      onCreate={() => navigate(`${listPath}/nova`)}
      onOpen={(row) => {
        const campaign = byId.get(row.id);
        if (!campaign) return;
        if (campaign.tipo_campanha === 'A') navigate(`${listPath}/${campaign._id}/editar`);
        else if (hasResults(campaign)) navigate(`${listPath}/${campaign._id}/resultados`);
      }}
      // Recorrente (fora aniversário) não edita nem tem resultados: o clique abria
      // o form, que só mostrava "não pode ser editada" e voltava pra lista.
      canOpen={(row) => {
        const campaign = byId.get(row.id);
        return Boolean(campaign && (campaign.tipo_campanha === 'A' || hasResults(campaign)));
      }}
      renderActions={(row) => {
        const campaign = byId.get(row.id);
        if (!campaign) return null;
        const showResults = hasResults(campaign);
        return (
          <PushActions
            campaign={campaign}
            onEdit={campaign.tipo_campanha === 'A' ? () => navigate(`${listPath}/${campaign._id}/editar`) : undefined}
            onPause={() => void setActive(campaign, campaign.active === false)}
            onRemove={campaign.tipo_campanha === 'A' ? undefined : () => void removeOne(campaign)}
            onResults={showResults ? () => navigate(`${listPath}/${campaign._id}/resultados`) : undefined}
          />
        );
      }}
    />
  );
}
